import React, { useState, useEffect } from 'react'
import { Box, Flex, Group, Button, Select, TextInput, NumberInput, FileInput, Image, Text, Paper } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconFileText } from '@tabler/icons-react'
import { getDetailUser } from '../../../../../services/users'
import { getPaymentCountry, getDetailPaymentChannel, updateProofPayment } from '../../../../../services/payment'
import LoadingData from '../../../../ui/LoadingData'
import { validation } from '../../../../../plugins/validation'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'
import dayjs from 'dayjs'

const defaultVal = {
  order_id: null,
  bank: '',
  account_name: '',
  account_number: '',
  nominal_transfer: 0,
  date_transfer: new Date(),
  transfer_to: '',
  proof_of_transfer: '',
}

const formValidation = {
  bank: {
    isError: false,
    message: ''
  },
  account_name: {
    isError: false,
    message: ''
  },
  account_number: {
    isError: false,
    message: ''
  },
  nominal_transfer: {
    isError: false,
    message: ''
  },
  date_transfer: {
    isError: false,
    message: ''
  },
}

const defaultTypeBank = [
  { label: 'BCA', value: 'BCA' },
  { label: 'HSBC', value: 'HSBC' },
  { label: 'BNI', value: 'BNI' },
  { label: 'BRI', value: 'BRI' },
  { label: 'Mandiri', value: 'MANDIRI' },
  { label: 'Permata', value: 'PERMATA' },
  { label: 'Credit Card', value: 'CREDIT_CARD' },
  { label: 'OVO', value: 'OVO' },
  { label: 'Other Bank', value: 'other_bank' },
]

const FormAdjustProofPayment = ({ dataPayment, onCloseForm, reloadList }) => {
  const [loadingPaymentCountry, setLoadingPaymentCountry] = useState(true)
  const [paymentList, setPaymentList] = useState([])
  const [formData, setFormData] = useState(defaultVal)
  const [validationForm, setValidationForm] = useState(formValidation)
  const [loadingForm, setLoadingForm] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = () => {
        const result = reader.result
        const commaIndex = result.indexOf(',')
        if (commaIndex !== -1) {
          resolve(result.substring(commaIndex + 1))
        } else {
          resolve(result)
        }
      }
      reader.onerror = error => reject(error)

      reader.readAsDataURL(file)
    })
  }

  useEffect(() => {
    if (!formData.proof_of_transfer) {
      setPreviewUrl('')
      return
    }

    if (typeof formData.proof_of_transfer === 'string') {
      setPreviewUrl(formData.proof_of_transfer)
    } else if (formData.proof_of_transfer instanceof File) {
      const objectUrl = URL.createObjectURL(formData.proof_of_transfer)
      setPreviewUrl(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    }
  }, [formData.proof_of_transfer])

  const isPdf = 
    (formData.proof_of_transfer instanceof File && formData.proof_of_transfer.type === 'application/pdf') ||
    (typeof formData.proof_of_transfer === 'string' && (
      formData.proof_of_transfer.toLowerCase().includes('.pdf') || 
      formData.proof_of_transfer.startsWith('data:application/pdf')
    ))

  const submitProofPayment = async (form) => {
    setLoadingForm(true)
    setValidationForm(formValidation)
    const validateForm = {
      bank: form.bank,
      account_name: form.account_name,
      account_number: form.account_number,
      nominal_transfer: form.nominal_transfer,
      date_transfer: form.date_transfer,
    }
    const isError = validation(validateForm, setValidationForm)
    if (isError) {
      setLoadingForm(false)
      return
    }
    const payload = {
      order_id: form.order_id ? Number(form.order_id) : null,
      bank: form.bank,
      transfer_to: form.transfer_to,
      customer_name: form.account_name,
      account_number: form.account_number,
      total_paid: form.nominal_transfer ? String(form.nominal_transfer) : '',
      transfer_date: form.date_transfer ? dayjs(form.date_transfer).format('YYYY-MM-DD') : '',
      proof_of_transfer_encoded: (form.proof_of_transfer && typeof form.proof_of_transfer === 'object')
        ? await fileToBase64(form.proof_of_transfer)
        : null,
    }
    try {
      const response = await updateProofPayment(dataPayment.id, payload)
      if (response.success) {
        notificationSuccess('Update Proof of Payment Successfully', 'You have been updated data proof of payment')
        onCloseForm()
        reloadList()
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response?.data?.message
      const captionMessageError = errorMessage ? (typeof errorMessage === 'object' ? Object.values(errorMessage).join(', ') : errorMessage) : 'Please recheck your form'
      notificationError('Update Proof of Payment Error', captionMessageError)
    } finally {
      setLoadingForm(false)
    }
  }

  const handleGetDetailUser = async (userId) => {
    setLoadingPaymentCountry(true)
    try {
      const response = await getDetailUser(userId)
      if (response.success) {
        const countrySignup = response.data.attributes.country_signup
        if (countrySignup !== null) {
          const getPaymentCountry = await handleGetPaymentCountry(countrySignup)
          if (getPaymentCountry.success) {
            const resPaymentCountry = getPaymentCountry.data

            const remapData = await Promise.all(resPaymentCountry.map(async (val) => {
              let data = {}
              const getPaymentChannel = await getDetailPaymentChannel(val.attributes.payment_channel_id)
              if (getPaymentChannel.success) {
                data = {
                  ...getPaymentChannel.data.attributes
                }
              }
              return data
            }))

            const findDuplicate = remapData.reduce((result, item) => {
              const removed = result.filter((val) => val.id !== item.id)
              return [...removed, item]
            }, [])

            const remapList = findDuplicate.map((val) => {
              return {
                label: val.name.replace(/VA/g, '').trim(),
                value: val.name.replace(/VA/g, '').trim(),
              }
            })

            setPaymentList(remapList)
          }
        }
      }
    } catch(error) {
      console.log('error', error)
      setPaymentList([])
    } finally {
      setLoadingPaymentCountry(false)
    }
  }

  const handleGetPaymentCountry = async (countryCode) => {
    const params = {
      'filter[country_id][is]': countryCode
    }
    try {
      const response = await getPaymentCountry(params)
      return response
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    if (dataPayment !== null) {
      handleSetForm(dataPayment)
    }
    // eslint-disable-next-line
  }, [dataPayment])

  const handleSetForm = (data) => {
    handleGetDetailUser(data.created_by)
    const detail = {
      order_id: data.order_id,
      bank: data.bank,
      account_name: data.customer_name,
      account_number: data.account_number,
      nominal_transfer: data.total_paid,
      date_transfer: data.transfer_date ? new Date(data.transfer_date) : '',
      transfer_to: data.transfer_to,
      proof_of_transfer: data.proof_of_transfer || '',
    }
    setFormData(detail)
  }

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const loadForm = () => {
    return (
      <Box>
        <Box mb='sm'>
          <Select
            name='bank'
            value={formData.bank}
            label='Transfer From'
            placeholder='Select Bank'
            data={defaultTypeBank}
            onChange={(val) => handleChangeForm('bank', val)}
            size='xs'
            allowDeselect={false}
            error={validationForm.bank.isError ? `${validationForm.bank.message}` : ''}
            withAsterisk
          />
        </Box>
        <Box my="sm">
          <TextInput
            name='account_name'
            value={formData.account_name}
            label='Account Name'
            error={validationForm.account_name.isError ? `${validationForm.account_name.message}` : ''}
            onChange={(val) => handleChangeForm('account_name', val.target.value)}
            withAsterisk
            size='xs'
          />
        </Box>
        <Box my="sm">
          <TextInput
            name='account_number'
            value={formData.account_number}
            label='Account Number'
            error={validationForm.account_number.isError ? `${validationForm.account_number.message}` : ''}
            onChange={(val) => handleChangeForm('account_number', val.target.value)}
            withAsterisk
            size='xs'
          />
        </Box>
        <Box my="sm">
          <NumberInput
            name='nominal_transfer'
            label="Nominal Transfer"
            value={formData.nominal_transfer}
            onChange={(val) => handleChangeForm('nominal_transfer', val)}
            error={validationForm.nominal_transfer.isError ? `${validationForm.nominal_transfer.message}` : ''}
            withAsterisk
            size='xs'
            thousandSeparator=","
            decimalSeparator='.'
            decimalScale={2}
          />
        </Box>
        <Box my="sm">
          <DateInput
            valueFormat="DD-MM-YYYY"
            value={formData.date_transfer}
            onChange={(val) => handleChangeForm('date_transfer', val)}
            error={validationForm.date_transfer.isError ? `${validationForm.date_transfer.message}` : ''}
            label="Transfer Date"
            placeholder="Date input"
            clearable
            size='xs'
            withAsterisk
          />
        </Box>
        <Box mb='sm'>
          <Select
            name='transfer_to'
            value={formData.transfer_to}
            label='Transfer To'
            placeholder='Select Bank'
            data={paymentList}
            onChange={(val) => handleChangeForm('transfer_to', val)}
            size='xs'
            allowDeselect={false}
          />
        </Box>
        <Box mb='sm'>
          <FileInput
            name='proof_of_transfer'
            value={formData.proof_of_transfer instanceof File ? formData.proof_of_transfer : null}
            label='Proof of Transfer'
            placeholder='Upload file'
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            onChange={(val) => handleChangeForm('proof_of_transfer', val)}
            size='xs'
            clearable
          />
        </Box>
        {previewUrl && (
          <Box my="sm">
            <Text fz="xs" fw={500} mb={4}>Preview:</Text>
            {isPdf ? (
              <Paper withBorder p="xs" radius="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconFileText size={24} color='gray' />
                <Box style={{ flex: 1 }}>
                  <Text fz="xs" fw={600} style={{ wordBreak: 'break-all' }}>
                    {formData.proof_of_transfer instanceof File ? formData.proof_of_transfer.name : 'Payment Receipt PDF'}
                  </Text>
                  <Button 
                    component="a" 
                    href={previewUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    size="compact-xs" 
                    variant="light" 
                    mt={4}
                  >
                    Open PDF
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper shadow="sm" w={182} withBorder radius='md' style={{ overflow: 'hidden' }}>
                <Image w={180} h={180} radius='md' src={previewUrl} fit="contain" />
              </Paper>
            )}
          </Box>
        )}
        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
              <Button size='xs' variant="filled" loading={loadingForm} onClick={() => submitProofPayment(formData)}>{dataPayment ? 'Update' : 'Create'}</Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {loadingPaymentCountry ? <LoadingData /> : loadForm()}
    </Box>
  )
}

export default FormAdjustProofPayment